import { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { ChevronDownIcon, ChevronUpIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

// Tree-table used for the items page, the see-inspection items list,
// and the flow-sheet hierarchical branch. Public API matches the
// legacy MUI version 1:1 — same props, same column shape, same
// helper exports — so the three call-sites don't need to change.
//
// Column shape (legacy):
//   { name: 'field', label: 'Header', options?: { customBodyRender(val, { rowIndex, value }) } }
//
// Indentation: every nested row gets `level * 20px` of left padding
// (`level * 15px` for the checkbox cell), matching the previous
// implementation; the only visual departure is shadcn Table chrome
// instead of the MUI Paper/Table primitives.

BFHierarchyTable.propTypes = {
  id: PropTypes.string,
  columns: PropTypes.array,
  data: PropTypes.any,
  leaf: PropTypes.string,
  page: PropTypes.number,
  onChangePage: PropTypes.func,
  expandableRows: PropTypes.bool,
  selectableRows: PropTypes.bool,
  fullSelector: PropTypes.bool,
  rowsSelected: PropTypes.array,
  onRowSelectionChange: PropTypes.func,
  /** Click a row (anywhere outside an interactive child element) to
   *  open it. Optional — when undefined, rows are not clickable. */
  onRowClick: PropTypes.func,
};

export default function BFHierarchyTable({
  id,
  columns,
  data,
  leaf,
  page,
  onChangePage,
  expandableRows,
  selectableRows,
  fullSelector,
  rowsSelected,
  onRowSelectionChange,
  onRowClick,
}) {
  const { t } = useTranslation();

  // Bubble up selection state when a child or parent box is toggled.
  // Mirrors the legacy propagation rules: ticking a parent ticks all
  // its leaves; unticking a parent only clears them when `fullSelector`
  // is set; parents auto-tick when all their leaves are selected (and
  // `fullSelector` is set).
  const rowsSelectedHandler = (checked, ID) => {
    let newRowsSelected = (rowsSelected ?? []).slice();
    const leafIds = getLeafsIds(data.items, leaf, ID);
    let parentId = getParentId(data.items, leaf, ID);

    if (checked) {
      newRowsSelected.push(ID);
    } else {
      newRowsSelected = newRowsSelected.filter((rid) => rid !== ID);
    }

    if (leafIds.length !== 0) {
      if (checked) {
        leafIds.forEach((lid) => {
          if (!newRowsSelected.includes(lid)) {
            newRowsSelected.push(lid);
          }
        });
      } else if (fullSelector) {
        newRowsSelected = newRowsSelected.filter((lid) => !leafIds.includes(lid));
      }
    }

    while (parentId !== null) {
      const parentLeafs = getLeafsIds(data.items, leaf, parentId.id);
      const allLeafsSelected = parentLeafs.every((lid) =>
        newRowsSelected.includes(lid),
      );
      const anyLeafUnchecked = parentLeafs.some(
        (lid) => !newRowsSelected.includes(lid),
      );

      if (allLeafsSelected && !anyLeafUnchecked && fullSelector) {
        newRowsSelected.push(parentId.id);
      } else {
        const parentIndex = newRowsSelected.indexOf(parentId.id);
        if (parentIndex !== -1) {
          newRowsSelected.splice(parentIndex, 1);
        }
      }

      parentId = getParentId(data.items, leaf, parentId.id);
    }

    onRowSelectionChange?.(newRowsSelected);
  };

  if (!data) return null;

  const totalCount = data?.itemCount ?? dataLength(data.items, leaf);
  const pageSize = data?.range?.limit ?? totalCount;
  const currentPage = page ?? 0;
  const pageCount = pageSize > 0 ? Math.max(1, Math.ceil(totalCount / pageSize)) : 1;

  return (
    <div className="space-y-3">
      <div className="rounded-md border">
        <Table key={id} aria-label="collapsible table">
          {columns ? (
            <TableHeader>
              <RowColumns
                data={getAllIds(data.items, leaf)}
                columns={columns}
                expandableRows={expandableRows}
                selectableRows={selectableRows}
                allChecked={
                  rowsSelected?.length === dataLength(data.items, leaf) &&
                  dataLength(data.items, leaf) > 0
                }
                indeterminate={
                  rowsSelected?.length > 0 &&
                  rowsSelected?.length < dataLength(data.items, leaf)
                }
                onRowSelectionChange={onRowSelectionChange}
              />
            </TableHeader>
          ) : null}
          <TableBody>
            {columns && data.itemCount > 0 ? (
              addLevels(data.items, leaf).map((row) => (
                <ExpandableRow
                  key={row.id}
                  item={row}
                  leaf={leaf}
                  columns={columns}
                  expandableRows={expandableRows}
                  selectableRows={selectableRows}
                  rowsSelected={rowsSelected}
                  rowsSelectedHandler={rowsSelectedHandler}
                  onRowClick={onRowClick}
                />
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={(columns?.length ?? 0) + 2}
                  className="h-24 text-center text-muted-foreground"
                >
                  {t("components.table.body.noMatch")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {onChangePage ? (
        <Pagination
          page={currentPage}
          pageCount={pageCount}
          onPageChange={onChangePage}
        />
      ) : null}
    </div>
  );
}

RowColumns.propTypes = {
  data: PropTypes.array,
  columns: PropTypes.array,
  expandableRows: PropTypes.bool,
  selectableRows: PropTypes.bool,
  allChecked: PropTypes.bool,
  indeterminate: PropTypes.bool,
  onRowSelectionChange: PropTypes.func,
};

function RowColumns({
  data,
  columns,
  expandableRows,
  selectableRows,
  allChecked,
  indeterminate,
  onRowSelectionChange,
}) {
  const [selectedAll, setSelectedAll] = useState(false);

  const selectedAllHandler = (checked) => {
    const next = Boolean(checked);
    setSelectedAll(next);
    onRowSelectionChange?.(next ? data : []);
  };

  return (
    <TableRow>
      {selectableRows ? (
        <TableHead className="w-10">
          <Checkbox
            checked={
              indeterminate ? "indeterminate" : allChecked ?? selectedAll
            }
            onCheckedChange={selectedAllHandler}
            aria-label="Select all"
          />
        </TableHead>
      ) : null}
      {columns.map((field) => (
        <TableHead key={field.name}>{field.label}</TableHead>
      ))}
      {expandableRows ? <TableHead /> : null}
    </TableRow>
  );
}

ExpandableRow.propTypes = {
  item: PropTypes.object,
  columns: PropTypes.array,
  leaf: PropTypes.string,
  expandableRows: PropTypes.bool,
  selectableRows: PropTypes.bool,
  rowsSelected: PropTypes.array,
  rowsSelectedHandler: PropTypes.func,
  onRowClick: PropTypes.func,
};

function ExpandableRow({
  item,
  columns,
  leaf,
  expandableRows,
  selectableRows,
  rowsSelected,
  rowsSelectedHandler,
  onRowClick,
}) {
  // Same legacy quirk: when `expandableRows` is undefined the row
  // starts open (consumers that pass `expandableRows={false}` get a
  // flat list).
  const [open, setOpen] = useState(expandableRows === undefined);
  let rowIndex = 0;

  const hasChildren = Array.isArray(item.children) && item.children.length > 0;
  const indent = (item?.level ?? 0) * 20;
  const checkboxIndent = (item?.level ?? 0) * 15;

  return (
    <>
      <TableRow
        className={cn(
          "hover:bg-muted/50",
          onRowClick && "cursor-pointer",
        )}
        onClick={onRowClick ? () => onRowClick(item) : undefined}
      >
        {selectableRows ? (
          <TableCell
            className="w-10"
            style={{ paddingLeft: checkboxIndent }}
            // Block the row-click bubbling so toggling the checkbox
            // doesn't also open the detail view.
            onClick={(e) => e.stopPropagation()}
          >
            <Checkbox
              checked={(rowsSelected ?? []).includes(item.id)}
              onCheckedChange={(checked) =>
                rowsSelectedHandler(Boolean(checked), item.id)
              }
              aria-label="Select row"
            />
          </TableCell>
        ) : null}

        {columns.map((column) => {
          rowIndex += 1;
          const cellStyle = { paddingLeft: indent };

          if (column.options?.customBodyRender) {
            return (
              <TableCell
                key={column.name}
                style={cellStyle}
                // Custom-rendered cells typically wrap interactive
                // elements (photo gallery, action buttons); keep
                // their clicks from triggering the row click.
                onClick={onRowClick ? (e) => e.stopPropagation() : undefined}
              >
                {column.options.customBodyRender(item[column.name], {
                  rowIndex,
                  value: item,
                })}
              </TableCell>
            );
          }

          return (
            <TableCell key={column.name} style={cellStyle}>
              {item[column.name]}
            </TableCell>
          );
        })}

        {expandableRows ? (
          <TableCell
            className="text-right"
            onClick={(e) => e.stopPropagation()}
          >
            {hasChildren ? (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setOpen((prev) => !prev)}
                aria-label={open ? "Collapse row" : "Expand row"}
              >
                {open ? (
                  <ChevronUpIcon className="size-4" />
                ) : (
                  <ChevronDownIcon className="size-4" />
                )}
              </Button>
            ) : null}
          </TableCell>
        ) : null}
      </TableRow>
      {open && Array.isArray(item[leaf])
        ? item[leaf].map((child) => (
            <ExpandableRow
              key={child.id}
              item={child}
              leaf={leaf}
              columns={columns}
              expandableRows={expandableRows}
              selectableRows={selectableRows}
              rowsSelected={rowsSelected}
              rowsSelectedHandler={rowsSelectedHandler}
              onRowClick={onRowClick}
            />
          ))
        : null}
    </>
  );
}

Pagination.propTypes = {
  page: PropTypes.number,
  pageCount: PropTypes.number,
  onPageChange: PropTypes.func,
};

function Pagination({ page, pageCount, onPageChange }) {
  // Same chrome as components/data-table's pagination row so the
  // hierarchical table feels identical to the flat one when a page
  // hosts both (e.g. resources + items in the same flow).
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between gap-2 text-sm text-muted-foreground">
      <span>
        {t("common.data-table.page-of", "Page {{current}} of {{total}}", {
          current: page + 1,
          total: pageCount || 1,
        })}
      </span>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange?.(page - 1)}
          disabled={page <= 0}
        >
          {t("common.back", "Previous")}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange?.(page + 1)}
          disabled={page + 1 >= pageCount}
        >
          {t("common.next", "Next")}
        </Button>
      </div>
    </div>
  );
}

// --- helpers (preserved verbatim; recursion-heavy by design) -----

const addLevels = (data, leaf, level = 1) => {
  try {
    return data.map((item) => {
      const newItem = { ...item, level };
      if (newItem[leaf] && newItem[leaf].length > 0) {
        newItem[leaf] = addLevels(newItem[leaf], leaf, level + 1);
      }
      return newItem;
    });
  } catch (error) {
    console.error("addLevels error: ", error);
    return data;
  }
};

export const getAllIds = (data, leaf) => {
  try {
    return data.reduce((ids, item) => {
      if (!ids.includes(item.id)) {
        ids.push(item.id);
        if (item[leaf] && item[leaf].length > 0) {
          const leafIds = getAllIds(item[leaf], leaf);
          ids.push(...leafIds);
        }
      }
      return ids;
    }, []);
  } catch (error) {
    console.error("getAllIds error: ", error);
    return [];
  }
};

const dataLength = (data, leaf) => {
  try {
    return data.reduce((count, item) => {
      let next = count + 1;
      if (item[leaf] && item[leaf].length > 0) {
        next += dataLength(item[leaf], leaf);
      }
      return next;
    }, 0);
  } catch (error) {
    console.error("dataLength error: ", error);
    return 0;
  }
};

function getParentId(data, leaf, id) {
  let parent = null;
  let stack = [...data];

  while (stack.length > 0) {
    const item = stack.shift();

    if (item[leaf] && item[leaf].length > 0) {
      stack = [...stack, ...item[leaf]];
    }

    if (item[leaf] && item[leaf].some((child) => child.id === id)) {
      parent = item;
      break;
    }
  }

  return parent;
}

export const getLeafsIds = (data, leaf, id) => {
  const result = [];

  try {
    const dataLoop = (items) => {
      items.forEach((item) => {
        if (item.id === id && item[leaf] && item[leaf].length > 0) {
          result.push(...getAllIds(item[leaf], leaf));
        }
        if (item[leaf] && item[leaf].length > 0) {
          dataLoop(item[leaf]);
        }
      });
    };

    dataLoop(data);
  } catch (error) {
    console.error("getLeafsIds error: ", error);
    return [];
  }

  return result;
};
